import PropTypes from 'prop-types';
import { includes, path } from 'ramda';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { SubmissionError } from 'redux-form';
import { readEndpoint, updateResource } from 'redux-json-api';

import CardContent from '@material-ui/core/CardContent';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';

import Business from '@material-ui/icons/Business';
import DirectionsBus from '@material-ui/icons/DirectionsBus';
import Person from '@material-ui/icons/Person';
import Timeline from '@material-ui/icons/Timeline';

import RideForm from './RideForm';

import { showSnackbarMessage } from '../../modules/actions/layout';
import { detailClose } from '../../modules/actions/rideAdmin';

const distanceFieldNames = [
  'distance-depot-start',
  'distance-shuttle-start',
  'distance-shuttle-end',
  'distance-depot-end',
];

const datetimeFieldNames = [
  'datetime-depot-start',
  'datetime-shuttle-start',
  'datetime-shuttle-end',
  'datetime-depot-end',
];

const locationFieldNames = [
  'location-shuttle-start',
  'location-shuttle-end'
];

const disruptionEndedFieldName = 'disruption-ended-shuttle-start'

const dateField = (field) => {
  const match = /^distance-(.+)$/.exec(field);

  if (match && match.length === 2) return `datetime-${match[1]}-date`;
};

const timeField = (field) => {
  const match = /^distance-(.+)$/.exec(field);

  if (match && match.length === 2) return `datetime-${match[1]}-time`;
};

const dateTimeField = (field) => {
  const match = /^distance-(.+)$/.exec(field);

  if (match && match.length === 2) return `datetime-${match[1]}`;
};

const translateError = (error) => {
  switch (error) {
    case 'Can\'t set distance_shuttle_start before distance_depot_start': {
      return 'De eerste kilometerstand dient eerst ingevuld te worden';
    }
    case 'Can\'t set distance_shuttle_end before distance_shuttle_start': {
      return 'De tweede kilometerstand dient eerst ingevuld te worden';
    }
    case 'Can\'t set distance_depot_end before distance_shuttle_end': {
      return 'De derde kilometerstand dient eerst ingevuld te worden';
    }
    case 'distance_shuttle_start can\'t be lower than distance_depot_start': {
      return 'De tweede kilometerstand mag niet lager zijn dan de eerste';
    }
    case 'distance_shuttle_end can\'t be lower than distance_shuttle_start': {
      return 'De derde kilometerstand mag niet lager zijn dan de tweede';
    }
    case 'distance_depot_end can\'t be lower than distance_shuttle_end': {
      return 'De vierde kilometerstand mag niet lager zijn dan de derde';
    }
    case 'Can\'t set location_shuttle_end before location_shuttle_start': {
      return 'De eerste locatie moet eerst ingevuld worden';
    }
    case 'You do not have permission to perform this action.': {
      return 'U heeft geen rechten om deze actie uit te voeren.';
    }
    default: {
      return error;
    }
  }
};

const RideEdit = ({ apiEndpoint, detailClose, open, options, showSnackbarMessage, updateResource }) => (
  <Dialog open={open} onClose={() => detailClose()} scroll="body">
    <DialogTitle>Rit bewerken</DialogTitle>

    {options && (
      <CardContent>
        <div>
          <span>
            <Timeline />
            {options.routeBlockageName}
          </span>

          <br />

          <span>
            <Business />
            {options.company.attributes.name}
          </span>

          <br />

          <span>
            <Person />
            {`${options.driver.attributes['first-name']} ${options.driver.attributes['last-name']}`}
          </span>

          <br />

          <span>
            <DirectionsBus />
            {`${options.vehicle.attributes['vehicle-number']} / ${options.vehicle.attributes['license-plate']}`}
          </span>
        </div>

        <RideForm
          stops={options.relatedStops}
          initialValues={{
            'datetime-depot-start-date': options.ride.attributes['datetime-depot-start'],
            'datetime-depot-start-time': options.ride.attributes['datetime-depot-start'],
            'datetime-shuttle-start-date': options.ride.attributes['datetime-shuttle-start'],
            'datetime-shuttle-start-time': options.ride.attributes['datetime-shuttle-start'],
            'datetime-shuttle-end-date': options.ride.attributes['datetime-shuttle-end'],
            'datetime-shuttle-end-time': options.ride.attributes['datetime-shuttle-end'],
            'datetime-depot-end-date': options.ride.attributes['datetime-depot-end'],
            'datetime-depot-end-time': options.ride.attributes['datetime-depot-end'],

            'distance-depot-start': options.ride.attributes['distance-depot-start'],
            'distance-shuttle-start': options.ride.attributes['distance-shuttle-start'],
            'distance-shuttle-end': options.ride.attributes['distance-shuttle-end'],
            'distance-depot-end': options.ride.attributes['distance-depot-end'],

            'disruption-ended-shuttle-start': options.ride.attributes['disruption-ended-shuttle-start'],
            'location-shuttle-start': options.ride.relationships &&
              options.ride.relationships['location-shuttle-start'].data ?
              options.ride.relationships['location-shuttle-start'].data.id : null,
            'location-shuttle-end': options.ride.relationships &&
              options.ride.relationships['location-shuttle-end'].data ?
              options.ride.relationships['location-shuttle-end'].data.id : null,
          }}
          onSubmit={async (values) => {
            const resource = {
              type: 'ride',
              id: options.ride.id,
              attributes: {},
            };

            distanceFieldNames.forEach((field) => {
              if (values[field] !== null &&
                  values[field] !== undefined &&
                  values[field] !== '') {
                resource.attributes[field] = +(values[field]);

                /**
                 * When changing the value of a distance field, or when keeping
                 * it the same, update the datetime field associated with it.
                 * If the date and time fields have been untouched, they're
                 * Strings, and otherwise they're Dates.
                 *
                 * If we don't submit either the old datetime or a new one when
                 * setting the distance, the API will calculate a new datetime
                 * based on the current date and time on the server, overriding
                 * any previously set values.
                 */
                const fieldDateValue = values[dateField(field)];
                const fieldTimeValue = values[timeField(field)];

                if (fieldDateValue && fieldTimeValue) {
                  const dateValue = fieldDateValue.constructor === Date ?
                    fieldDateValue : new Date(fieldDateValue);
                  const timeValue = fieldTimeValue.constructor === Date ?
                    fieldTimeValue : new Date(fieldTimeValue);

                  const year = dateValue.getFullYear();
                  const month = dateValue.getMonth();
                  const date = dateValue.getDate();
                  const hours = timeValue.getHours();
                  const minutes = timeValue.getMinutes();

                  const newDate = new Date(year, month, date, hours, minutes);

                  resource.attributes[dateTimeField(field)] = newDate;
                }
              }
            });

            locationFieldNames.forEach(field => {
              if (values[field] !== null) {
                resource.attributes[field] = `${apiEndpoint}/stop/${values[field]}`;
              } else {
                resource.attributes[field] = null;
              }

              resource.attributes[disruptionEndedFieldName] = values[disruptionEndedFieldName]
            })

            updateResource(resource)
              .then(() => {
                detailClose();
                showSnackbarMessage('Rit succesvol bewerkt');
              })
              .catch(error => {
                const response = error.response.data
                const errors = {};

                if (response.errors) {
                  const fieldMatch = /^\/data\/attributes\/([A-Za-z0-9-_]+)$/;

                  response.errors.forEach((error) => {
                    const fieldPath = path(['source', 'pointer'], error) || '';
                    const match = fieldMatch.exec(fieldPath);
                    const detail = translateError(error.detail);

                    if (match && match.length === 2) {
                      const fieldName = match[1];

                      if (fieldName === 'non_field_errors') errors._error = detail;
                      else if (includes(fieldName, distanceFieldNames) || includes(fieldName, locationFieldNames)) errors[fieldName] = detail;
                      else if (includes(fieldName, datetimeFieldNames)) {
                        /**
                         * Set the error on both the date- and the time-picker.
                         */
                        errors[`${fieldName}-date`] = detail;
                        errors[`${fieldName}-time`] = detail;
                      } else errors._error = detail;
                    } else {
                      if (detail) errors._error = detail;
                      else errors._error = 'Er is iets fout gegaan bij het bewerken van de rit.';
                    }
                  });
                } else {
                  errors._error = 'Er is iets fout gegaan bij het bewerken van de rit.';
                }

                showSnackbarMessage(errors._error);
                throw new SubmissionError(errors);
              })
          }}
        />
      </CardContent>
    )}
  </Dialog>
);

RideEdit.propTypes = {
  // own props
  apiEndpoint: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  options: PropTypes.shape({
    company: PropTypes.shape({
      attributes: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
    }),

    driver: PropTypes.shape({
      attributes: PropTypes.shape({
        'first-name': PropTypes.string.isRequired,
        'last-name': PropTypes.string.isRequired,
      }).isRequired,
    }),

    ride: PropTypes.shape({
      id: PropTypes.string.isRequired,

      attributes: PropTypes.shape({
        'distance-depot-start': PropTypes.number,
        'distance-shuttle-start': PropTypes.number,
        'distance-shuttle-end': PropTypes.number,
        'distance-depot-end': PropTypes.number,
      }).isRequired,
    }).isRequired,

    routeBlockageName: PropTypes.string.isRequired,

    vehicle: PropTypes.shape({
      attributes: PropTypes.shape({
        'license-plate': PropTypes.string.isRequired,
        'vehicle-number': PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
  }),

  // dispatch props
  detailClose: PropTypes.func.isRequired,
  showSnackbarMessage: PropTypes.func.isRequired,
  updateResource: PropTypes.func.isRequired,
};

RideEdit.defaultProps = {
  options: {
    company: { attributes: { name: 'Onbekend' } },
    driver: { attributes: { 'first-name': 'Onbekend', 'last-name': 'Onbekend' } },
    relatedStops: [{}],
    ride: { id: '', attributes: {} },
    routeBlockageName: '--',
    vehicle: { attributes: { 'license-plate': '--', 'vehicle-number': 0 } },
  },
};

const mapStateToProps = (state) => ({
  apiEndpoint: path(['api', 'endpoint', 'axiosConfig', 'baseURL'], state),
});

export default connect(mapStateToProps, { detailClose, readEndpoint, showSnackbarMessage, updateResource })(RideEdit);
